import { Fragment, type ReactNode } from "react";

/**
 * Renders the markdown subset the assistant is instructed to emit: paragraphs,
 * **bold**, *italic*, `inline code`, fenced code blocks, "- "/"1. " lists and
 * simple pipe tables. Built entirely from React elements — never HTML strings —
 * so hostile model output can only ever become literal text.
 */

type Block =
  | { kind: "paragraph"; lines: string[] }
  | { kind: "code"; text: string }
  | { kind: "list"; ordered: boolean; items: string[] }
  | { kind: "table"; header: string[]; rows: string[][] };

const unorderedItem = /^[-*] /;
const orderedItem = /^\d+\. /;
const tableSeparator = /^\|[\s|:-]+\|$/;

function isTableStart(line: string, next: string): boolean {
  return line.startsWith("|") && tableSeparator.test(next.trim());
}

function cells(line: string): string[] {
  return line
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());
}

function parseBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  const lines = text.split("\n");
  for (let index = 0; index < lines.length;) {
    const line = lines[index] ?? "";
    if (line.trim() === "") {
      index += 1;
      continue;
    }
    if (line.startsWith("```")) {
      const body: string[] = [];
      index += 1;
      while (index < lines.length && !(lines[index] ?? "").startsWith("```")) {
        body.push(lines[index] ?? "");
        index += 1;
      }
      index += 1; // Past the closing fence, or past the end when unclosed.
      blocks.push({ kind: "code", text: body.join("\n") });
      continue;
    }
    if (unorderedItem.test(line) || orderedItem.test(line)) {
      const ordered = orderedItem.test(line);
      const pattern = ordered ? orderedItem : unorderedItem;
      const items: string[] = [];
      while (index < lines.length && pattern.test(lines[index] ?? "")) {
        items.push((lines[index] ?? "").replace(pattern, ""));
        index += 1;
      }
      blocks.push({ kind: "list", ordered, items });
      continue;
    }
    if (isTableStart(line, lines[index + 1] ?? "")) {
      const header = cells(line);
      index += 2;
      const rows: string[][] = [];
      while (index < lines.length && (lines[index] ?? "").startsWith("|")) {
        rows.push(cells(lines[index] ?? ""));
        index += 1;
      }
      blocks.push({ kind: "table", header, rows });
      continue;
    }
    const paragraph: string[] = [];
    while (index < lines.length) {
      const current = lines[index] ?? "";
      if (
        current.trim() === "" ||
        current.startsWith("```") ||
        unorderedItem.test(current) ||
        orderedItem.test(current) ||
        isTableStart(current, lines[index + 1] ?? "")
      ) {
        break;
      }
      paragraph.push(current);
      index += 1;
    }
    blocks.push({ kind: "paragraph", lines: paragraph });
  }
  return blocks;
}

function emphasis(text: string, prefix: string): ReactNode[] {
  return text
    .split(/(\*\*[^*]+\*\*|\*[^*]+\*)/)
    .filter((part) => part !== "")
    .map((part, index) => {
      const key = `${prefix}-e${String(index)}`;
      if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
        return <strong key={key}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
        return <em key={key}>{part.slice(1, -1)}</em>;
      }
      return <Fragment key={key}>{part}</Fragment>;
    });
}

/** Code spans split first so markers inside backticks stay literal. */
function inline(text: string, prefix: string): ReactNode[] {
  return text
    .split(/(`[^`]+`)/)
    .filter((part) => part !== "")
    .map((part, index) => {
      const key = `${prefix}-i${String(index)}`;
      if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
        return <code key={key}>{part.slice(1, -1)}</code>;
      }
      return <Fragment key={key}>{emphasis(part, key)}</Fragment>;
    });
}

export function renderMarkdown(text: string): ReactNode {
  return parseBlocks(text).map((block, index) => {
    const key = `b${String(index)}`;
    if (block.kind === "code") {
      return (
        <pre key={key}>
          <code>{block.text}</code>
        </pre>
      );
    }
    if (block.kind === "list") {
      const items = block.items.map((item, position) => {
        const itemKey = `${key}-l${String(position)}`;
        return <li key={itemKey}>{inline(item, itemKey)}</li>;
      });
      return block.ordered ? (
        <ol key={key}>{items}</ol>
      ) : (
        <ul key={key}>{items}</ul>
      );
    }
    if (block.kind === "table") {
      return (
        <div key={key} className="assistant__table">
          <table>
            <thead>
              <tr>
                {block.header.map((cell, position) => {
                  const cellKey = `${key}-h${String(position)}`;
                  return <th key={cellKey}>{inline(cell, cellKey)}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => {
                const rowKey = `${key}-r${String(rowIndex)}`;
                return (
                  <tr key={rowKey}>
                    {row.map((cell, position) => {
                      const cellKey = `${rowKey}-c${String(position)}`;
                      return <td key={cellKey}>{inline(cell, cellKey)}</td>;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
    return <p key={key}>{inline(block.lines.join(" "), key)}</p>;
  });
}
